import React, { useState, useRef, useEffect } from "react";
import {
  Heart,
  MessageCircle,
  Layers,
  Star,
  Clock,
  Bookmark,
} from "lucide-react";
import CollectionMenu from "./CollectionMenu";
import {
  addToCollection,
  removeFromCollection,
} from "../../../services/collectionService";
import { useAuth } from "../../../context/AuthContext";
import ErrorModal from "../../Modal/ErrorModal";

// ✅ Utility function to format likes count (keep as is)
const formatLikesCount = (count) => {
  if (!count || count === 0) return "0";

  const num = parseInt(count);

  if (num < 1000) return num.toString();

  if (num < 1000000) {
    const thousands = num / 1000;
    if (thousands < 100) {
      return `${thousands.toFixed(1).replace(/\.0$/, "")}K`;
    }
    return `${Math.floor(thousands)}K`;
  }

  const millions = num / 1000000;
  if (millions < 100) {
    return `${millions.toFixed(1).replace(/\.0$/, "")}M`;
  }
  return `${Math.floor(millions)}M`;
};

const PostActions = ({
  post,
  isLiked,
  isBookmarked,
  handleLike,
  handleBookmark,
  onShowComments,
  onShowLikes,
  onCollectionChange,
}) => {
  const { apiCall } = useAuth();

  // ✅ UPDATED: Use privacySettings directly from post object
  const canLike = post.privacySettings?.allowLikes === 1;
  const canComment = post.privacySettings?.allowComments === 1;

  // ✅ State for collection menu
  const [isCollectionMenuOpen, setIsCollectionMenuOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
  const collectionButtonRef = useRef(null);

  // ✅ Initialize from post.collectionType prop
  const [activeCollection, setActiveCollection] = useState(
    post.collectionType || null
  );
  const [isLoading, setIsLoading] = useState(false);
  const [errorModal, setErrorModal] = useState({ isOpen: false, message: "" });

  // ✅ Update when post.collectionType changes
  useEffect(() => {
    setActiveCollection(post.collectionType || null);
  }, [post.collectionType]);

  // ✅ Handle collection button click
  const handleCollectionClick = () => {
    if (collectionButtonRef.current) {
      const rect = collectionButtonRef.current.getBoundingClientRect();
      setMenuPosition({
        top: rect.top + rect.height / 2,
        left: rect.left,
      });
      setIsCollectionMenuOpen(true);
      document.body.style.overflow = "hidden";
    }
  };

  // ✅ Handle collection menu close
  const handleCollectionClose = () => {
    setIsCollectionMenuOpen(false);
    document.body.style.overflow = "unset";
  };

  const handleCollectionSelect = async (optionId) => {
    if (isLoading) return;

    setIsLoading(true);

    const previousCollection = activeCollection;

    try {
      const isCurrentlyActive = activeCollection === optionId;

      if (isCurrentlyActive) {
        // Toggle off - Remove from collection
        setActiveCollection(null);
        await removeFromCollection(apiCall, post.id);
        if (onCollectionChange) {
          onCollectionChange(post.id, null);
        }
      } else {
        // Add or update collection
        setActiveCollection(optionId);
        await addToCollection(apiCall, post.id, optionId);
        if (onCollectionChange) {
          onCollectionChange(post.id, optionId);
        }
      }
    } catch (error) {
      setActiveCollection(previousCollection);
      setErrorModal({
        isOpen: true,
        message: error.message || "We couldn't update your collection. Please try again.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // ✅ UPDATED: Handle like with privacy check
  const handleLikeClick = () => {
    if (!canLike) {
      return;
    }
    handleLike(post.id);
  };

  // ✅ UPDATED: Handle comment with privacy check
  const handleCommentClick = () => {
    if (!canComment) {
      return;
    }
    onShowComments(post.id);
  };

  // ✅ Get the icon and color based on active collection
  const getCollectionIcon = () => {
    if (isLoading) {
      return { Icon: Layers, color: "text-gray-400" };
    }

    switch (activeCollection) {
      case "favourite":
        return { Icon: Star, color: "text-yellow-500" };
      case "read-later":
        return { Icon: Clock, color: "text-white" };
      case "save":
        return { Icon: Bookmark, color: "text-purple-500" };
      default:
        return { Icon: Layers, color: "text-gray-900" };
    }
  };

  const { Icon: CollectionIcon, color: collectionColor } = getCollectionIcon();

  return (
    <>
      <div className="px-4">
        {/* Sleek Action Bar */}
        <div className="flex items-center gap-4 py-2">
          {/* Like Button */}
          <button
            onClick={handleLikeClick}
            className={`group flex items-center gap-2 transition-all duration-200 ${
              !canLike ? "opacity-60 cursor-default" : ""
            }`}
          >
            <div className={`relative`}>
              <Heart
                size={!canLike ? 20 : 24}
                strokeWidth={2}
                className={`transition-all duration-300 ${
                  isLiked
                    ? "fill-red-500 text-red-500 scale-110"
                    : !canLike
                    ? "text-gray-400 scale-90"
                    : "text-gray-900 group-hover:text-red-500 group-hover:scale-110"
                }`}
              />
            </div>
            {canLike && post.likes > 0 && (
              <span className="text-base font-semibold text-gray-900">
                {formatLikesCount(post.likes)}
              </span>
            )}
            {!canLike && (
              <span className="text-xs text-gray-400 font-medium">
                Likes off
              </span>
            )}
          </button>
          {/* Comment Button */}
          <button
            onClick={handleCommentClick}
            className={`group flex items-center gap-2 transition-all duration-200 ${
              !canComment ? "opacity-60 cursor-default" : ""
            }`}
          >
            <MessageCircle
              size={!canComment ? 20 : 24}
              strokeWidth={2}
              className={`transition-all duration-300 ${
                !canComment
                  ? "text-gray-400 scale-90"
                  : "text-gray-900 group-hover:text-blue-500 group-hover:scale-110"
              }`}
            />
            {canComment && post.comments > 0 && (
              <span className="text-base font-semibold text-gray-900">
                {post.comments}
              </span>
            )}
            {!canComment && (
              <span className="text-xs text-gray-400 font-medium">
                Comments off
              </span>
            )}
          </button>

          {/* Spacer */}
          <div className="flex-1"></div>

          {/* Collection Button */}
          <button
            ref={collectionButtonRef}
            onClick={handleCollectionClick}
            disabled={isLoading}
            className="group flex items-center gap-1.5 transition-all duration-200 disabled:opacity-50"
          >
            <CollectionIcon
              size={24}
              strokeWidth={2}
              className={`transition-all duration-300 ${
                activeCollection
                  ? "text-black scale-110"
                  : "text-black group-hover:scale-110"
              } ${isCollectionMenuOpen ? "scale-110" : ""}`}
            />
          </button>
        </div>

        {/* View Likes Button - Only show if likes are allowed AND likes count > 0 */}
        {canLike && post.likes > 0 && (
          <button
            onClick={() => onShowLikes(post.id)}
            className="pb-3 text-sm font-bold text-gray-900 hover:text-gray-700 transition-colors"
          >
            View {formatLikesCount(post.likes)} likes
          </button>
        )}

        {/* ✅ Show message when comments are disabled but there are existing comments */}
        {!canComment && post.comments > 0 && (
          <div className="pb-3">
            <span className="text-xs text-gray-400 italic">
              Comments are disabled
            </span>
          </div>
        )}
      </div>

      {/* Collection Menu */}
      <CollectionMenu
        isOpen={isCollectionMenuOpen}
        onClose={handleCollectionClose}
        onSelect={handleCollectionSelect}
        position={menuPosition}
        activeCollection={activeCollection}
      />
      <ErrorModal
        isOpen={errorModal.isOpen}
        message={errorModal.message}
        onClose={() => setErrorModal({ isOpen: false, message: "" })}
      />
    </>
  );
};

export default PostActions;